import React, { FunctionComponent } from 'react'

import { Trans, useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { Text } from '@fairhq/common'
import { ENPS } from 'features/reportsDiversity/ENPS'
import { PermissionsEnum, useAuthPermissions } from 'hooks/useAuthPermissions'
import { Layout } from 'layout/Layout'
import { useGetSurveyWindowArchiveComparisonQuery } from 'store/surveyWindow/surveyWindowApi'

import RouterLink from 'ui-kit/RouterLink'

import { CompanyMakeUp } from './CompanyMakeup'
import { DiversityOverview } from './DiversityOverview'
import { KeyIndustryDemographics } from './KeyIndustryDemographics'

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

export const ReportsDiversity: FunctionComponent = () => {
  const { code: jobLevel } = useParams<{ code: string }>()

  const { permissions } = useAuthPermissions()
  const { t } = useTranslation()

  const { data, isSuccess } = useGetSurveyWindowArchiveComparisonQuery()

  const latestSurveyWindow = data?.[0]
  const previousSurveyWindow = data?.[1]

  if (isSuccess && !latestSurveyWindow) {
    return <Navigate to="/404" />
  }

  return (
    <Layout
      contentSx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}
      dark
    >
      <Header>
        <Text element="h1" type="heading" size="xl" fontWeight={700}>
          {!jobLevel && t(`diversity.title`)}
          {jobLevel && t(`jobLevel.${jobLevel.toLowerCase()}`)}
        </Text>

        {isSuccess && latestSurveyWindow && (
          <Text element="p" type="body" size="xs" fontWeight={400}>
            <Trans
              i18nKey="diversity.overview.headerInfo"
              components={
                permissions.includes(PermissionsEnum.READ_SURVEYWINDOW)
                  ? [<RouterLink key="survey" to="/survey" />]
                  : []
              }
            />
          </Text>
        )}
      </Header>

      {isSuccess && latestSurveyWindow && (
        <>
          {!jobLevel && (
            <ENPS
              latestSurveyWindowId={latestSurveyWindow.id}
              previousSurveyWindowId={previousSurveyWindow?.id}
            />
          )}

          <DiversityOverview
            latestSurveyWindow={latestSurveyWindow}
            level={jobLevel}
          />

          <KeyIndustryDemographics
            latestSurveyWindow={latestSurveyWindow}
            previousSurveyWindow={previousSurveyWindow}
            level={jobLevel}
          />

          <CompanyMakeUp
            latestSurveyWindow={latestSurveyWindow}
            level={jobLevel}
          />
        </>
      )}
    </Layout>
  )
}
